import { initReactI18next, useTranslation } from "react-i18next";

import cs from "./cs.json";
import czechMessages from "ra-language-czech";
import dayjs from "dayjs";
import de from "./de.json";
import en from "./en.json";
import englishMessages from "ra-language-english";
import germanMessages from "ra-language-german";
import i18next from "i18next";
import { merge } from "lodash";
import { resolveBrowserLocale } from "react-admin";
import sk from "./sk.json";
import slovakMessages from "ra-language-slovak";

export enum LANGUAGE {
    DE = "de",
    SK = "sk",
    CZ = "cs",
    EN = "en",
}

export const FALLBACK_LANGUAGE = LANGUAGE.CZ;

export const languageCode = (
    localStorage.getItem("locale") ||
    resolveBrowserLocale() ||
    "cs"
).toLowerCase();

dayjs.locale(languageCode);

i18next
    .use(initReactI18next)
    .init({
        fallbackLng: FALLBACK_LANGUAGE,
        debug: false,
        ns: ["index", "static"],
        defaultNS: "index",
        fallbackNS: ["static"],
        partialBundledLanguages: true,
        lng: languageCode,
        pluralSeparator: "_",
        interpolation: {
            escapeValue: false, // React already escapes values
            prefix: "%{",
            suffix: "}",
        },
        react: {
            useSuspense: false,
        },
    });

i18next.addResourceBundle("en", "static", merge({}, englishMessages, en));
i18next.addResourceBundle("cs", "static", merge({}, czechMessages, cs));
i18next.addResourceBundle("sk", "static", merge({}, slovakMessages, sk));
i18next.addResourceBundle("de", "static", merge({}, germanMessages, de));

i18next.changeLanguage(languageCode);

const getMergedMessages = async (locale: string): Promise<Record<string, any>> => {
    const localMessages: Record<string, any> =
        {
            en: merge(englishMessages, en),
            cs: merge(czechMessages, cs),
        }[locale] || englishMessages;

    try {
        const remoteMessages = await i18next.services.resourceStore.data[locale];
        return { ...localMessages, ...remoteMessages };
    } catch (error) {
        console.error("Failed to load Locize translations", error);
        return localMessages;
    }
};

const customI18nProvider = {
    translate: (key: string, options?: any): string => {
        if (!key) return "";
        return i18next.t(key, options).toString();
    },
    changeLocale: async (locale: string): Promise<any> => {
        localStorage.setItem("locale", locale);
        window.location.reload();

        return getMergedMessages(locale);
    },
    getLocale: () => i18next.language || "en",
};

export const useTranslate = () => {
    const { t } = useTranslation();
    return (key: string, options?: any): string => {
        return key ? String(t(key, options)) : "";
    };
};
export default customI18nProvider;
